import { useEffect, useMemo, useRef } from 'react';
import { useSearchResultQuery } from '../queries/useSearchResultQuery';
import { useSearchResultMetaQuery } from '../queries/useSearchResultMetaQuery';
import { Flex, Spinner } from '@banhala/ui-kit';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';
import { useScrollToFilterList } from 'screens/src/components/hooks/useScrollToFilterList';
import { AtomicRenderer, useFilterListAtom } from 'shared/src/component';
import { useLogViewEvent } from '../hook/useLogViewEvent';
import { useIsForegroundNow } from 'shared/src/hooks';
import type { ListRef } from '@banhala/ui-kit';
import type { IScreensComponent } from 'shared/src/api';

// iOS: BaseViewController dimmedSpinnerView — 필터 적용 중 리스트 영역을 덮는
// 반투명(gray10 #F5F5F5 + alpha 0.4) 오버레이 + 중앙 스피너(gray50). Flex.Center를
// createAnimatedComponent로 감싸 reanimated FadeIn/FadeOut(0.3초)을 적용한다.
const AnimatedFlexCenter = Animated.createAnimatedComponent(Flex.Center);

type SearchResultListProps = {
  screenName: string;
  query: string;
  nextToken?: string;
  onScrollY?: (scrollY: number) => void;
  onScrollEndWorklet?: (scrollY: number) => void;
};

/**
 * 검색 결과 상품 리스트
 * - SearchResult 페이지 전용
 * - 검색어 기반 무한 스크롤 + 필터 연동
 */
export const SearchResultList = ({
  screenName,
  query,
  nextToken,
  onScrollY,
  onScrollEndWorklet,
}: SearchResultListProps) => {
  const listRef = useRef<ListRef<IScreensComponent> | null>(null);
  const isForeground = useIsForegroundNow();

  const {
    data,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useSearchResultQuery({
    screenName,
    query,
    nextToken,
  });

  // iOS `MetaFeedReactor.fetchScreenAppreanceData` / Android `ComponentMetaViewModel`
  // 와 1:1 대칭. SEARCH_RESULT는 서버가 "3col"로 내려줘 3col 스켈레톤을 표시.
  const { data: skeletonType } = useSearchResultMetaQuery(screenName);

  // 지연 발화 대기 중 자동 prefetch — iOS의 willDisplay 기반 auto-loadmore와 동일한 의도
  // (VIEW 이벤트 유실 방지). prefetch 조건 자체는 select에서 계산됨.
  useEffect(() => {
    if (data?.shouldAutoPrefetchNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [data?.shouldAutoPrefetchNextPage, isFetchingNextPage, fetchNextPage]);

  // iOS: MetaFeedReactor.trackViewLoggingEvent → VIEW_{screenName}
  useLogViewEvent(data);

  // 필터/검색어 변경 시 AtomicRenderer의 displayHistoryRef 캐시 reset 트리거.
  // - 필터 적용: filterListAtomValue 객체 reference 변경
  // - 자동완성/연관검색어 inline 클릭(같은 인스턴스 내 query 변경): query 변경
  // - 재검색(close→open): 새 인스턴스로 자연 mount/unmount
  const { filterListAtomValue } = useFilterListAtom();
  const filterParams = useMemo(
    () => ({ ...filterListAtomValue, query }),
    [filterListAtomValue, query],
  );

  const itemHeightsRef = useRef<{ [key: number]: number }>({});
  const {
    filterListIndex,
    scrollToFilterList,
    onItemHeightChange,
    isPendingScroll,
  } = useScrollToFilterList({
    atomicRendererRef: listRef,
    components: data?.components,
    isFetching,
    itemHeightsRef,
  });

  if (isLoading) {
    return (
      <Flex.Center flex={1}>
        <Spinner size={20} />
      </Flex.Center>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <>
      {/* 로딩 중엔 항상 중앙 스피너를 띄우되, dim 배경(gray10 0.4)과 터치 차단은
          필터 적용 시에만 적용(iOS dimmedCover 정합). 페이지네이션 시엔 dim/터치차단
          없이 중앙 스피너만 노출해 스크롤을 막지 않는다. */}
      {isFetching && (
        <AnimatedFlexCenter
          entering={FadeIn.duration(300)}
          exiting={FadeOut.duration(300)}
          position="absolute"
          top={0}
          bottom={0}
          left={0}
          right={0}
          backgroundColor={isFetchingNextPage ? undefined : 'gray10'}
          backgroundColorOpacity={isFetchingNextPage ? undefined : 0.4}
          pointerEvents={isFetchingNextPage ? 'none' : 'auto'}
          zIndex={1000}
        >
          <Spinner size={20} color="content_tertiary" />
        </AnimatedFlexCenter>
      )}
      <AtomicRenderer
        refetch={refetch}
        data={data}
        fetchNextPage={fetchNextPage}
        listRef={listRef}
        filterListIndex={filterListIndex}
        scrollToFilterList={scrollToFilterList}
        isFetching={isFetching}
        isPendingScroll={isPendingScroll}
        filterParams={filterParams}
        itemHeightsRef={itemHeightsRef}
        onItemHeightChange={onItemHeightChange}
        onScrollY={onScrollY}
        skeletonType={skeletonType}
        onScrollEndWorklet={onScrollEndWorklet}
        isForeground={isForeground}
      />
    </>
  );
};
